import type { Api, MenuItem } from '@/api'
import { SiteConfig } from '@/model'
import { defineStore } from 'pinia'
import { computed, inject, nextTick, reactive, ref } from 'vue'
import { useFairStore } from './fair'
import { canDisplay, menuItemsRequireFairId } from '@/router/menu'
import { useRoute, useRouter } from 'vue-router'
import { useLayout } from '@/composables/layout'
import { useI18n } from 'vue-i18n'
import { useCompanyStore } from './company'
import { useExhibitorStore } from './exhibitor'

const { hideMenu } = useLayout()

export const useSiteStore = defineStore('siteStore', () => {
  const api = inject('exhibitorApi') as Api<unknown>

  const { locale } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const menuItems = ref<MenuItem[]>([])
  const siteConfig = reactive<SiteConfig>(new SiteConfig(globalThis.initOptions.siteConfig))
  const pageReady = ref(false)
  const isDividedFacilityMenu = ref(false)
  const isDividedPromotionNameMenu = ref(true)
  const isBmsRegisterMenu = ref(false)
  const { showMenu } = useLayout()
  const fairStore = useFairStore()
  const companyStore = useCompanyStore()
  const organizerInfo = computed(() => ({
    companyName: common.isKorean() ? siteConfig.organizerName : siteConfig.organizerNameEng,
    ceoName: common.isKorean() ? siteConfig.organizerCeoName : siteConfig.organizerCeoNameEng,
    bizNo: siteConfig.organizerBizNo,
    address: common.isKorean() ? siteConfig.organizerAddress : siteConfig.organizerAddressEng,
    zipCode: siteConfig.organizerZipCode,
    faxNo: siteConfig.organizerFaxNo,
    telNo: siteConfig.organizerTelNo,
    email: siteConfig.organizerEmail,
    homepage: siteConfig.organizerHomepage,
    copyright: siteConfig.organizerNameEng
  }))
  const isCompany = computed(() => ['기업', '업체', '회사'].some((str) => siteConfig.exhibitorLabel?.includes(str)))

  const currentMenu = computed(() =>
    menuItems.value
      .flatMap((mi) => mi.items ?? [])
      .find((item) => {
        if (!item.to) {
          return
        }

        const itemRoute = router.resolve({ name: item.to as string, params: item.parameters as any })
        return itemRoute.path == route.path
      })
  )

  async function refreshMenu() {
    const fairStore = useFairStore()
    const fairId = fairStore.lastFairId

    if (fairId === 0) {
      return
    }

    // 이전에 다른 전시 사이트에서 선택했던 fairId로 메뉴를 조회하는 문제 수정
    const lastFairMasterId = await api.fair
      .fairDetail(fairId)
      .then((res) => res.data.fairMasterId)
      .catch(() => null)

    if (lastFairMasterId === null || fairStore.fair?.fairMasterId !== lastFairMasterId) {
      hideMenu()
      return
    }

    const items = await api.common
      .menuItemsList({
        fairMasterId: fairStore.fairMasterId ?? siteConfig.fairMasterId,
        fairId: fairId,
        locale: locale.value as 'ko' | 'en'
      })
      .then((res) => res.data)

    isDividedFacilityMenu.value = items.flatMap((mi) => mi.items ?? []).some((item) => item.to === 'support-request_facility')
    isDividedPromotionNameMenu.value = items.flatMap((mi) => mi.items ?? []).some((item) => item.to === 'support-brand')
    isBmsRegisterMenu.value = items.flatMap((mi) => mi.items ?? []).some((item) => item.to === 'bms-register')

    menuItems.value = []

    if (fairStore.fair?.id === undefined) {
      items.forEach((mi) => {
        mi.items = mi.items?.filter((item) => item.to && !menuItemsRequireFairId.includes(item.to as string))

        if (mi.items && mi.items.length > 0) {
          menuItems.value.push(mi)
        }
      })
    } else {
      const exhibitorStore = useExhibitorStore()
      const exhibitor = exhibitorStore.exhibitor
      items.forEach((mi) => {
        mi.items = mi.items?.filter((item) => canDisplay(item, exhibitor) || !item.to)

        if (mi.items && mi.items.length > 0) {
          menuItems.value.push(mi)
        }
      })
    }

    await nextTick()
    pageReady.value = true

    if (window.innerWidth > 991) {
      showMenu()
    }
  }

  const initialize = async (forcedRefresh: boolean = false) => {
    if (pageReady.value && !forcedRefresh) {
      return
    }

    pageReady.value = false

    companyStore.initCompany()
    await fairStore.refreshFairs()

    if (route.params.FAIR_ID !== undefined && parseInt(route.params.FAIR_ID as string)) {
      await fairStore.changeCurrentFair(parseInt(route.params.FAIR_ID as string))
    } else if (fairStore.lastFairId) {
      await fairStore.changeCurrentFair(fairStore.lastFairId)
    } else {
      hideMenu()
    }
  }

  const goBack = () => {
    if (window.history.length > 2) {
      window.history.back()
    } else {
      router.push({ name: 'root-fair_list' })
    }
  }

  const logoUrl = computed(() => {
    if (locale.value === 'en') {
      return siteConfig.logoUrlEng ?? siteConfig.logoUrl
    }

    return siteConfig.logoUrl
  })

  const contactInfo = computed(() => {
    if (locale.value === 'en') {
      return siteConfig.contactEmail ?? siteConfig.contactTel
    }
    return siteConfig.contactTel
  })

  const changeLocale = (newLocale: 'ko' | 'en') => {
    // 지원하는 언어가 1개이고, lastLocale가 지원하는 언어가 아닐 경우
    if (siteConfig.supportLocales.length == 1 && !siteConfig.supportLocales.includes(newLocale)) {
      locale.value = siteConfig.supportLocales[0]
      localStorage.setItem('lastLocale', siteConfig.supportLocales[0])
    } else {
      locale.value = newLocale
      localStorage.setItem('lastLocale', newLocale)
    }

    document.querySelector('html')?.setAttribute('lang', newLocale)
  }

  return {
    menuItems,
    currentMenu,
    siteConfig,
    refreshMenu,
    initialize,
    pageReady,
    isDividedFacilityMenu,
    isDividedPromotionNameMenu,
    goBack,
    logoUrl,
    changeLocale,
    contactInfo,
    organizerInfo,
    isCompany,
    isBmsRegisterMenu
  }
})
